"use client";

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Text } from "../../atoms/text/text";
import { TOOLTIP_ATTRIBUTE } from "./indicator.shared";
import { useTooltipDismiss } from "./use-tooltip-dismiss";
import styles from "./indicator.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * A client boundary, and why this one is earned: AX6 asks that tabbing to an indicator or a logo
 * reveal its tooltip and Escape hide it — the ARIA APG tooltip pattern, which no native element
 * provides (`title` is not keyboard-reachable, `popovertarget` needs a press, not focus).
 */

/* Mirrors `--spacing-8` in indicator.module.css — the trigger-to-panel gap and the viewport inset.
   Nothing at runtime links the two. */
const SPACING_8 = 8;

const SCOPE_ATTRIBUTE = "data-cl-tip-scope";

/** Measures before paint; `useEffect` on the server, where the layout variant warns. */
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

interface ActiveTip {
  /** The trigger root carrying `TOOLTIP_ATTRIBUTE`. */
  root: HTMLElement;
  button: HTMLButtonElement;
  content: string;
  /** False when the wording only repeats the button's name. */
  describes: boolean;
}

export interface TooltipScopeProps {
  children: React.ReactNode;
}

function TooltipScopeImpl({ children }: TooltipScopeProps) {
  const [active, setActive] = useState<ActiveTip | null>(null);
  const panelId = useId();
  const scopeRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);

  const close = useCallback(() => setActive(null), []);
  useTooltipDismiss(active !== null, close);

  /** The trigger root an event came from, if THIS scope owns it — a nested
   *  scope handles its own triggers and this one stays out. */
  const rootOf = (target: EventTarget | null): HTMLElement | null => {
    if (!(target instanceof Element)) {
      return null;
    }
    const root = target.closest<HTMLElement>(`[${TOOLTIP_ATTRIBUTE}]`);
    if (!root || root.closest(`[${SCOPE_ATTRIBUTE}]`) !== scopeRef.current) {
      return null;
    }
    return root;
  };

  const open = (root: HTMLElement) => {
    const button = root.querySelector("button");
    if (!button) {
      return;
    }
    const tip = root.getAttribute(TOOLTIP_ATTRIBUTE) ?? "";
    const next: ActiveTip = {
      root,
      button,
      content: tip || (button.getAttribute("aria-label") ?? ""),
      describes: tip !== ""
    };
    // Same trigger keeps the same object, so a pointer moving over it re-renders nothing.
    setActive(current => (current?.root === root ? current : next));
  };

  /* The description exists only while the panel does, as with a per-trigger
     panel: written to the button imperatively because the button is server
     markup this component does not render. */
  useEffect(() => {
    if (!active?.describes) {
      return undefined;
    }
    const { button } = active;
    button.setAttribute("aria-describedby", panelId);
    return () => button.removeAttribute("aria-describedby");
  }, [active, panelId]);

  /* Top layer, then placed in viewport coordinates. A lineup cell sits inside a horizontally
     scrolling grid, so an absolutely positioned panel would be clipped by the scroller; `popover`
     escapes every ancestor's overflow and transform. Written to the panel's style directly: the
     position feeds nothing React renders, so it costs no second render. */
  useIsomorphicLayoutEffect(() => {
    const panel = panelRef.current;
    if (!active) {
      return;
    }
    if (!panel) {
      return;
    }
    if (typeof panel.showPopover === "function") {
      try {
        panel.showPopover();
      } catch {
        /* Already open, or unsupported — the panel still renders inline. */
      }
    }
    const anchor = active.button.getBoundingClientRect();
    const box = panel.getBoundingClientRect();
    const maxLeft = Math.max(SPACING_8, window.innerWidth - box.width - SPACING_8);
    panel.style.top = `${anchor.bottom + SPACING_8}px`;
    panel.style.left = `${Math.min(Math.max(SPACING_8, anchor.left + anchor.width / 2 - box.width / 2), maxLeft)}px`;
  }, [active]);

  return (
    <div
      className={styles.scope}
      {...{ [SCOPE_ATTRIBUTE]: "" }}
      onBlur={event => {
        const root = rootOf(event.target);
        if (!root || root !== active?.root) {
          return;
        }
        if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) {
          return;
        }
        close();
      }}
      onClick={event => {
        const root = rootOf(event.target);
        if (!root) {
          return;
        }
        if (active?.root === root) {
          close();
        } else {
          open(root);
        }
      }}
      onFocus={event => {
        const root = rootOf(event.target);
        if (root) {
          open(root);
        }
      }}
      onPointerOut={event => {
        // Touch opens through the click toggle; a touch leave must not undo it.
        if (event.pointerType === "touch") {
          return;
        }
        const root = rootOf(event.target);
        if (!root || root !== active?.root) {
          return;
        }
        if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) {
          return;
        }
        close();
      }}
      onPointerOver={event => {
        // An unguarded touch enter would open, then the tap's click would close.
        if (event.pointerType === "touch") {
          return;
        }
        const root = rootOf(event.target);
        if (root) {
          open(root);
        }
      }}
      ref={scopeRef}
    >
      {children}
      {active && (
        <span
          className={styles.panel}
          id={panelId}
          popover="manual"
          ref={panelRef}
          role="tooltip"
          style={{
            /* Off-screen until measured — a paint before layout flashes at 0,0.
               `right`/`bottom`/`margin` override the popover UA defaults, which
               would otherwise centre the panel in the viewport. */
            top: -9999,
            left: -9999,
            right: "auto",
            bottom: "auto",
            margin: 0
          }}
        >
          <Text as="span" variant="body-3">
            {active.content}
          </Text>
        </span>
      )}
    </div>
  );
}
export const TooltipScope = withSafeRender(TooltipScopeImpl, "TooltipScope");

export default TooltipScope;
